import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuthContext } from "@/contexts/AuthContext";

export interface QRStats {
  totalScans: number;
  totalQRCodes: number;
  todayScans: number;
  weekScans: number;
  uniqueUsers: number;
  deviceBreakdown: { device: string; count: number; percent: number }[];
  countryBreakdown: { country: string; count: number; percent: number }[];
  recentScans: {
    id: string;
    scannedAt: string;
    qrName: string;
    country: string;
    city: string;
    device: string;
    browser: string;
    ipHash: string;
  }[];
  dailyScans: { date: string; scans: number }[];
}

export function useQRStats() {
  const { user } = useAuthContext();
  const [stats, setStats] = useState<QRStats | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);

    const fetchStats = async () => {
      const { data: qrCodes } = await supabase
        .from("qr_codes")
        .select("id, name, scans")
        .eq("user_id", user.id);

      const qrIds = qrCodes?.map((q) => q.id) || [];
      const totalQRCodes = qrCodes?.length || 0;
      const totalScans = qrCodes?.reduce((sum, q) => sum + (q.scans || 0), 0) || 0;

      const { data: scans } = await supabase
        .from("scan_logs")
        .select("id, scanned_at, country, city, device, browser, ip_hash, qr_code_id")
        .in("qr_code_id", qrIds)
        .order("scanned_at", { ascending: false });

      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - 7);

      let todayScans = 0;
      let weekScans = 0;
      const dailyMap: Record<string, number> = {};
      const deviceMap: Record<string, number> = {};
      const countryMap: Record<string, number> = {};
      const ipSet = new Set<string>();
      const recent: QRStats["recentScans"] = [];

      if (scans) {
        for (const s of scans) {
          const scannedAt = new Date(s.scanned_at);
          const date = scannedAt.toLocaleDateString("tr-TR", { day: "numeric", month: "short" });
          dailyMap[date] = (dailyMap[date] || 0) + 1;

          if (scannedAt >= todayStart) todayScans++;
          if (scannedAt >= weekStart) weekScans++;

          const d = s.device || "Bilinmiyor";
          deviceMap[d] = (deviceMap[d] || 0) + 1;

          const c = s.country || "Bilinmiyor";
          countryMap[c] = (countryMap[c] || 0) + 1;

          if (s.ip_hash) ipSet.add(s.ip_hash);

          const qr = qrCodes?.find((q) => q.id === s.qr_code_id);
          recent.push({
            id: s.id,
            scannedAt: s.scanned_at,
            qrName: qr?.name || "Bilinmiyor",
            country: s.country || "-",
            city: s.city || "-",
            device: s.device || "-",
            browser: s.browser || "-",
            ipHash: s.ip_hash || "-",
          });
        }
      }

      const totalDevice = Object.values(deviceMap).reduce((a, b) => a + b, 0) || 1;
      const deviceBreakdown = Object.entries(deviceMap)
        .map(([device, count]) => ({ device, count, percent: Math.round((count / totalDevice) * 100) }))
        .sort((a, b) => b.count - a.count);

      const totalCountry = Object.values(countryMap).reduce((a, b) => a + b, 0) || 1;
      const countryBreakdown = Object.entries(countryMap)
        .map(([country, count]) => ({ country, count, percent: Math.round((count / totalCountry) * 100) }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      const dailyScans = Object.entries(dailyMap)
        .map(([date, scans]) => ({ date, scans }))
        .reverse()
        .slice(-7);

      setStats({
        totalScans,
        totalQRCodes,
        todayScans,
        weekScans,
        uniqueUsers: ipSet.size,
        deviceBreakdown,
        countryBreakdown,
        recentScans: recent.slice(0, 50),
        dailyScans,
      });
      setLoading(false);
    };

    fetchStats();
  }, [user]);

  return { stats, loading };
}